import { PlanMealRootItemType } from "../model/nutritionPlanTypes";

/** A meal's header text and the items to lay out. */
export type MealToPack<T = PlanMealRootItemType> = {
  type: number;
  label: string;
  time: string;
  isLogged: boolean;
  items: T[];
};

/**
 * One run of a meal's items on one grid row, with the header that spans
 * them. A meal with more items than the row has room for continues on the
 * next row as a further segment with the same header.
 */
export type MealSegment<T = PlanMealRootItemType> = {
  meal: MealToPack<T>;
  /** 0-based card row. */
  row: number;
  /** 0-based first column. */
  column: number;
  /** Columns taken: the number of items, or 1 for an empty meal's placeholder. */
  span: number;
  items: T[];
};

/**
 * Pack the day's meals, in order, into `columns` columns: a meal joins the
 * current row only when all of its items fit in the cells left there;
 * otherwise it starts a fresh row, so a meal is never split just to plug a
 * gap. A meal with more items than a row holds starts a fresh row and wraps
 * across as many rows as it needs. An empty meal keeps one cell so its
 * header still shows.
 */
export const packMealGrid = <T>(
  meals: MealToPack<T>[],
  columns: number
): MealSegment<T>[] => {
  const segments: MealSegment<T>[] = [];
  let row = 0;
  let column = 0;

  const advance = (cells: number) => {
    column += cells;
    if (column >= columns) {
      column = 0;
      row += 1;
    }
  };

  for (const meal of meals) {
    if (meal.items.length === 0) {
      segments.push({ meal, row, column, span: 1, items: [] });
      advance(1);
      continue;
    }
    if (column > 0 && meal.items.length > columns - column) {
      column = 0;
      row += 1;
    }
    let remaining = meal.items;
    while (remaining.length > 0) {
      const take = Math.min(columns - column, remaining.length);
      segments.push({
        meal,
        row,
        column,
        span: take,
        items: remaining.slice(0, take),
      });
      remaining = remaining.slice(take);
      advance(take);
    }
  }

  return segments;
};
